'use client';

import { ReactNode } from 'react';
import Link from 'next/link';
import { User } from '@supabase/supabase-js';
import {
  IconBlockquote,
  IconFeatherFilled,
  IconHelpHexagon,
  IconHome,
  IconMap,
  IconProgress,
  IconReport,
  IconShieldLock,
  IconUserCircle,
  IconUsersGroup,
} from '@tabler/icons-react';
import { AppShell, Burger, Group, Image, NavLink, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Profile } from '@/app/types/database';
import AuthButtons from '../Buttons/AuthButtons';
import AnnouncementIndicator from '../Indicators/AnnouncementIndicator';

interface AppShellWrapperProps {
  children: ReactNode;
  profile: Profile | null;
  user: User | null;
}

export default function AppShellWrapper({ user, profile, children }: AppShellWrapperProps) {
  const [opened, { toggle }] = useDisclosure();

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 200,
        breakpoint: 'sm',
        collapsed: { mobile: !opened },
      }}
      padding="md"
    >
      <AppShell.Header>
        <Group justify="space-between">
          <Link href="/">
            <Image mt={6} ml={10} h={40} w="auto" src="/logo.png" alt="Logo" />
          </Link>
          <Burger mt={8} mr={8} opened={opened} onClick={toggle} hiddenFrom="sm" size="lg" />
          <Group visibleFrom="md">
            <AuthButtons user={user} opened toggle={toggle} />
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar>
        <Stack mt={12} align="stretch" gap="xs">
          <NavLink
            href="/"
            color="black"
            label="Home"
            active
            variant="subtle"
            leftSection={<IconHome stroke={1.5} />}
          />
          <Group>
            <NavLink
              href="/announcement"
              color="black"
              label="Announcements"
              active
              variant="subtle"
              leftSection={<IconBlockquote stroke={1.5} />}
              rightSection={
                <AnnouncementIndicator profileAnnouncement={profile?.last_announcements || null} />
              }
            />
          </Group>
          <NavLink
            href="/map"
            color="black"
            label="Map"
            active
            variant="subtle"
            leftSection={<IconMap stroke={1.5} />}
          />
          <NavLink
            href="/dispatch"
            color="black"
            label="Dispatch"
            active
            variant="subtle"
            leftSection={<IconFeatherFilled stroke={1.5} />}
          />
          {(profile?.admin || profile?.is_staff) && (
            <NavLink
              href={`/wip`}
              color="black"
              label="WIP"
              active
              variant="subtle"
              leftSection={<IconProgress stroke={1.5} />}
            />
          )}
          {user && (
            <>
              <NavLink
                href={`/profile/${user.id}`}
                color="black"
                label="Profile"
                active
                variant="subtle"
                leftSection={<IconUserCircle stroke={1.5} />}
              />
              {/* <NavLink
                href={`/team-support`}
                color="black"
                label="Team Support"
                active
                variant="subtle"
                leftSection={<IconUsersGroup stroke={1.5} />}
              /> */}
            </>
          )}
          <NavLink
            href={`/faq`}
            color="black"
            label="FAQ"
            active
            variant="subtle"
            leftSection={<IconHelpHexagon stroke={1.5} />}
          />
          {user && profile && profile.admin && (
            <>
              <NavLink
                href="/admin"
                color="black"
                label="Admin"
                active
                variant="subtle"
                leftSection={<IconShieldLock stroke={1.5} />}
              />
              <NavLink
                href="/reports"
                color="black"
                label="Reports"
                active
                variant="subtle"
                leftSection={<IconReport stroke={1.5} />}
              />
            </>
          )}
        </Stack>
        <Stack align="flex-start" justify="flex-end" hiddenFrom="sm" style={{ height: '100%' }}>
          <AuthButtons user={user} opened toggle={toggle} />
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>{children}</AppShell.Main>
    </AppShell>
  );
}
